import { HttpClient } from '@angular/common/http';
import { Component, ViewEncapsulation } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { Html2CanvasOptions, HTMLFontFace, jsPDF } from 'jspdf';
import { firstValueFrom } from 'rxjs';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { CdkScrollable } from '@angular/cdk/scrolling';
import { NgIf } from '@angular/common';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';

import { pageTransitions } from '../../../../shared/animations/page-transitions';
import {
	StepClickedEvent,
	StepStatus,
	StepperComponent
} from '../../../../shared/components/stepper/stepper.component';
import { LocationService } from '../../../../shared/services/location.service';
import { TranslationService } from '../../../../shared/services/translation.service';
import { Guarantee } from '../../../models/guarantee';
import { GuaranteeStatus } from '../../../models/guarantee-constants';
import { GuaranteeRequestService } from '../../../services/guarantee-request.service';
import { SnackbarSaveDraftGuaranteeComponent } from '../../utils/snackbar-save-draft-guarantee.component';
import { SnackbarSubmitGuaranteeComponent } from '../../utils/snackbar-submit-guarantee.component';
import { LoadingSpinnerComponent } from '../../../../shared/components/utils/loading-spinner.component';
import { GuaranteeRequestSummaryComponent } from '../../guarantee-request-summary/guarantee-request-summary.component';
import { PageHeaderComponent } from '../../../../shared/components/page-header/page-header.component';
import { DatePipe } from '../../../../shared/pipes/date.pipe';

import { LoadingDialogComponent } from './loading-dialog.component';

const fontFaces: HTMLFontFace[] = [
	{
		family: 'Roboto',
		weight: 300,
		style: 'italic',
		src: [
			{
				url: 'assets/fonts/roboto-lightitalic.ttf',
				format: 'truetype'
			}
		]
	},
	{
		family: 'Roboto',
		weight: 400,
		src: [
			{
				url: 'assets/fonts/roboto-regular.ttf',
				format: 'truetype'
			}
		]
	},

	{
		family: 'VAG Rounded LT CYR',
		weight: 300,
		src: [
			{
				url: 'assets/fonts/vag2-webfont.ttf',
				format: 'truetype'
			}
		]
	},
];

async function blobToBase64(blob: Blob) {
	return new Promise<string>((resolve, _) => {
		const reader = new FileReader();
		reader.onloadend = () => {
			const result = <string> reader.result;
			resolve(result.slice(result.indexOf('base64,') + 7));
		};
		reader.readAsDataURL(blob);
	});
}

function measureRenderedHeight(element: HTMLElement, width: number): number {
	const clone = <HTMLElement> element.cloneNode(true);
	clone.style.position = 'fixed';
	clone.style.visibility = 'hidden';
	clone.style.left = '-10000px';
	clone.style.top = '0';
	clone.style.width = `${ width }px`;
	document.body.append(clone);
	const style = getComputedStyle(element);
	const height = clone.scrollHeight
		+ (Number.parseFloat(style.marginTop) || 0)
		+ (Number.parseFloat(style.marginBottom) || 0);
	clone.remove();
	return height;
}

interface RowGroup {
	rows: HTMLElement[];
	height: number;
}

function getRowGroups(section: HTMLElement, width: number): RowGroup[] {
	const rows = <HTMLElement[]> Array.from(section.children);
	const heights = rows.map(row => measureRenderedHeight(row, width));
	const groups: RowGroup[] = [];
	let i = 0;
	while (i < rows.length) {
		const isPartsFollowedByAdditionalComments = section.classList.contains('guarantee-parts-block')
			&& rows[i].classList.contains('guarantee-parts-block-item')
			&& i + 1 < rows.length
			&& rows[i + 1].classList.contains('guarantee-other-material-block-item');
		const isSubtitle = rows[i].classList.contains('guarantee-details-block-subtitle');
		const groupSize = isPartsFollowedByAdditionalComments
			? 2
			: isSubtitle && i + 1 < rows.length
				? 2
				: 1;
		groups.push({
			rows: rows.slice(i, i + groupSize),
			height: heights.slice(i, i + groupSize).reduce((sum, height) => sum + height, 0)
		});
		i += groupSize;
	}
	return groups;
}

function normalizePageOps(page: string[] | undefined): string[] {
	return (page ?? []).map(line => line.trim()).filter(line => line.length > 0);
}

function matchesPageSignature(page: string[] | undefined, signature: string[]): boolean {
	const normalizedPage = normalizePageOps(page);
	if (normalizedPage.length !== signature.length) {
		return false;
	}
	return normalizedPage.every((line, index) => line === signature[index]);
}

@Component({
	selector: 'copo-guarantee-request-confirmation',
	templateUrl: './guarantee-request-confirmation.component.html',
	styleUrl: './guarantee-request-confirmation.component.css',
	animations: pageTransitions,
	encapsulation: ViewEncapsulation.Emulated,
	imports: [
		NgIf,
		CdkScrollable,
		PageHeaderComponent,

		MatButtonModule,
		TranslateModule,
		MatIconModule,
		StepperComponent,
		GuaranteeRequestSummaryComponent,
		LoadingSpinnerComponent
	]
})
export class GuaranteeRequestConfirmationComponent {
	submitting = false;

	guarantee: Guarantee;
	summaryElements?: HTMLElement[];

	readonly steps;
	dialogRef: MatDialogRef<LoadingDialogComponent> | undefined;
	private readonly projectNameMaxLength = 35;

	constructor(
		private readonly route: ActivatedRoute,
		private readonly locationService: LocationService,
		private readonly translationService: TranslationService,
		private readonly guaranteeService: GuaranteeRequestService,
		private readonly router: Router,
		private readonly httpClient: HttpClient,

		private readonly snackBar: MatSnackBar,
		private readonly translateService: TranslateService,
		private readonly dialog: MatDialog,
	) {
		this.steps = [
			{
				label: this.translationService.getTR('guarantee.project.characteristics'),
				status: StepStatus.ENABLED,
			},
			{
				label: this.translationService.getTR('guarantee.project.environment'),
				status: StepStatus.ENABLED,
			},
			{
				label: this.translationService.getTR('guarantee.type'),
				status: StepStatus.ENABLED,
			},
			{
				label: this.translationService.getTR('guarantee.summary'),
				status: StepStatus.CURRENT,
			}
		];
		const res = <{ guarantee: Guarantee }> this.route.snapshot.data;
		this.guarantee = res['guarantee'];
	}

	async saveGuarantee(event: MouseEvent) {
		event.preventDefault();

		this.dialogRef = this.dialog.open<LoadingDialogComponent>(
			LoadingDialogComponent,
			{ disableClose: true, minWidth: '256px', autoFocus: false }
		);
		let blobData: Blob | undefined;
		let guaranteeId: string | undefined;
		let guaranteeRecordId: string | undefined;
		let requestDate: string | undefined;
		try {
			if (event.ctrlKey) {
				// CTRL preview: no CRM creation, so the Guarantee ID does not exist yet.
				blobData = await this.generateEnglishPdf('XXX');
				const link = document.createElement('a');
				link.href = URL.createObjectURL(blobData);
				link.target = '_blank';
				link.append(document.createTextNode('guarantee-request.pdf'));
				document.body.append(link);
				link.click();
				link.remove();
				return;
			}
			// Create in CRM first to obtain the real Guarantee ID shown in the PDF header.
			const savedGuarantee = await this.guaranteeService.saveGuarantee(this.guarantee);
			guaranteeId = savedGuarantee.guaranteeId;
			guaranteeRecordId = savedGuarantee.id;
			requestDate = savedGuarantee.requestDate;
			if (guaranteeId && this.guarantee.status === GuaranteeStatus.OPEN) {
				blobData = await this.generateEnglishPdf(guaranteeId, requestDate);
			}
		} finally {
			this.dialogRef.close();
			this.dialogRef = undefined;
		}
		if (guaranteeRecordId && guaranteeId && blobData && this.guarantee.status === GuaranteeStatus.OPEN) {
			try {
				this.submitting = true;
				const guaranteeFile = 'guarrantee-request';
				const fileName = `${ guaranteeId }-${ guaranteeFile }.pdf`;
				await this.guaranteeService.uploadGuaranteeFile(blobData, fileName, guaranteeRecordId);
			} finally {
				this.submitting = false;
			}
		}
		this.guaranteeService.guarantee = new Guarantee();
		if (this.guarantee.status === GuaranteeStatus.DRAFT) {
			await this.router.navigate(['/guarantee-requests/drafts'], { replaceUrl: true });
			this.snackBar.openFromComponent(
				SnackbarSaveDraftGuaranteeComponent,
				{
					duration: 5000,
					panelClass: ['copo-claim-draft-snackbar']
				}
			);
		} else {
			await this.router.navigate(['/guarantee-requests/list'], { replaceUrl: true });
			this.snackBar.openFromComponent(
				SnackbarSubmitGuaranteeComponent,
				{
					duration: 5000,
					panelClass: ['copo-claim-draft-snackbar'],
					data: this.guarantee
				}
			);
		}
	}

	async submitGuarantee(event: MouseEvent) {
		this.guarantee.status = GuaranteeStatus.OPEN;

		try {
			await this.saveGuarantee(event);
		} catch (err) {
			this.guarantee.status = GuaranteeStatus.DRAFT;
			console.error(err);
			this.snackBar.openFromComponent(
				SnackbarSaveDraftGuaranteeComponent,
				{
					duration: 5000,
					panelClass: ['copo-claim-draft-snackbar'],
					data: true
				}
			);
		}
	}

	onSummaryElementsCreated(elements: HTMLElement[]) {
		this.summaryElements = elements;
	}

	goBack() {
		this.locationService.go(-1, this.guarantee.id !== undefined
			? `../guarantee-requests/edit/${ this.guarantee.id }/type-of-guarantee`
			: '../guarantee-requests/new/type-of-guarantee');
	}

	print() {
		print();
	}

	async generateGuaranteePdf(guaranteeId: string, requestDate = this.guarantee.requestDate) {
		if (!this.summaryElements) {
			throw new Error('summary elements not created');
		}
		const elementsToRender = this.summaryElements.filter(element => element.getBoundingClientRect().height > 0);

		const doc = new jsPDF({
			orientation: 'p',
			unit: 'px',
			hotfixes: ['px_scaling'],
			putOnlyUsedFonts: true,
			format: 'a4',
			compress: true,
		});

		for (const { fontFace, font } of await Promise.all(
			fontFaces.map(async ff => ({
				fontFace: ff,
				font: await blobToBase64(await firstValueFrom(
					this.httpClient.get(ff.src[0].url, { withCredentials: false, responseType: 'blob' })
				))
			}))
		)) {
			doc.addFileToVFS(fontFace.src[0].url, font);
			doc.addFont(
				fontFace.src[0].url,
				fontFace.family,
				`${ fontFace.stretch || 'normal' } ${ fontFace.style || 'normal' } ${ fontFace.weight || 400 }`
			);
		}

		const htmlTopMargin = 66;
		const htmlRightMargin = 60;
		const htmlBottomMargin = 24;
		const htmlLeftMargin = 40;
		const usablePageHeight = doc.internal.pageSize.height - htmlTopMargin - htmlBottomMargin;
		const renderWidth = 750;
		const renderWindowWidth = 690;
		const renderScale = renderWidth / renderWindowWidth;
		const pageBreakBuffer = 4;
		const packablePageHeight = usablePageHeight - pageBreakBuffer;
		let cursorY = 0;
		for (const elementToRender of elementsToRender) {
			const elements = Array.from(elementToRender.querySelectorAll('mat-icon'));
			for (const element of elements) {
				(<HTMLElement>element).style.display = 'none';
			}

			const previousMargin = elementToRender.style.margin;
			elementToRender.style.margin = '0';

			const cardItems = Array.from(elementToRender.querySelectorAll<HTMLElement>(
				'.guarantee-parts-block-item, .guarantee-other-material-block-item'
			));
			const previousCardPaddingLeft = cardItems.map(item => item.style.paddingLeft);
			const previousCardMarginBottom = cardItems.map(item => item.style.marginBottom);
			for (const item of cardItems) {
				item.style.paddingLeft = '0';
			}
			for (const item of cardItems) {
				item.style.marginBottom = '0';
			}

			const allRows = <HTMLElement[]> Array.from(elementToRender.children);
			const rowGroups = getRowGroups(elementToRender, renderWindowWidth);

			let groupIndex = 0;
			while (groupIndex < rowGroups.length) {
				const chunkRows: HTMLElement[] = [];
				let chunkHeight = 0;
				while (groupIndex < rowGroups.length) {
					const group = rowGroups[groupIndex];
					const groupHeight = group.height * renderScale;
					const fits = cursorY + chunkHeight + groupHeight <= packablePageHeight;
					if (chunkRows.length > 0 && !fits) {
						break;
					}
					if (chunkRows.length === 0 && cursorY > 0 && !fits) {
						// Doesn't fit in what's left of the current page - start a fresh one for it.
						doc.addPage();
						cursorY = 0;
					}
					chunkRows.push(...group.rows);
					chunkHeight += groupHeight;
					groupIndex++;
				}

				const hiddenRows = allRows.filter(row => !chunkRows.includes(row));
				const previousDisplay = hiddenRows.map(row => row.style.display);
				for (const row of hiddenRows) {
					row.style.display = 'none';
				}

				const pagesBeforeRender = doc.getNumberOfPages();
				await doc.html(
					elementToRender,
					{

						margin: [htmlTopMargin, htmlRightMargin, htmlBottomMargin, htmlLeftMargin],

						y: (pagesBeforeRender - 1) * usablePageHeight + cursorY,

						width: renderWidth,
						windowWidth: renderWindowWidth,

						html2canvas: <Html2CanvasOptions> {
							javascriptEnabled: false
						},
						fontFaces
					}
				);

				for (const [index, row] of hiddenRows.entries()) {
					row.style.display = previousDisplay[index];
				}

				const pagesAfterRender = doc.getNumberOfPages();
				cursorY = pagesAfterRender > pagesBeforeRender ? 0 : cursorY + chunkHeight;
			}

			for (const element of elements) {
				(<HTMLElement>element).style.display = '';
			}
			elementToRender.style.margin = previousMargin;
			for (const [index, item] of cardItems.entries()) {
				item.style.paddingLeft = previousCardPaddingLeft[index];
			}
			for (const [index, item] of cardItems.entries()) {
				item.style.marginBottom = previousCardMarginBottom[index];
			}
		}

		let pageCount = doc.getNumberOfPages();
		for (let i = 0; i < 10; i++) {
			await new Promise<void>(resolve => {
				setTimeout(resolve, 20);
			});
			const currentPageCount = doc.getNumberOfPages();
			if (currentPageCount === pageCount) {
				break;
			}
			pageCount = currentPageCount;
		}

		doc.addPage();
		const pages = <string[][]> <unknown> doc.internal.pages;
		const blankPageSignature = normalizePageOps(pages[doc.getNumberOfPages()]);
		doc.deletePage(doc.getNumberOfPages());

		let lastPageNumber = doc.getNumberOfPages();
		while (lastPageNumber > 1 && matchesPageSignature(pages[lastPageNumber], blankPageSignature)) {
			doc.deletePage(lastPageNumber);
			lastPageNumber = doc.getNumberOfPages();
		}

		this.drawHeaderAndFooter(doc, guaranteeId, requestDate);

		return doc.output('blob');
	}

	async onStepClicked(event: StepClickedEvent) {
		if (event.stepIndex === 0) {
			await this.router.navigate(['../project-characteristics'], { relativeTo: this.route });
		}
		if (event.stepIndex === 1) {
			await this.router.navigate(
				['../project-environment'],
				{ relativeTo: this.route }
			);
		}
		if (event.stepIndex === 2) {
			await this.router.navigate(
				['../type-of-guarantee'],
				{ relativeTo: this.route }
			);
		}
	}

	getLongType() {
		switch (this.guarantee.applicationType) {
		case 'Magnelis-Solar':
			return ` - ${ this.translationService.getTR('guarantee.magnelissolar.long') }`;
		case 'Magnelis-Building':
			return ` - ${ this.translationService.getTR('guarantee.magnelisbuilding.long') }`;
		case 'Magnelis-Other application':
			return ` - ${ this.translationService.getTR('guarantee.magnelisothers.long') }`;
		case 'Organic Coating':
			return ` ${ this.translationService.getTR('guarantee.organiccoating.long') }`;
		}
		return '';
	}

	private async generateEnglishPdf(guaranteeId: string, requestDate?: string): Promise<Blob> {
		const currentLanguage = this.translateService.currentLang;
		await firstValueFrom(this.translateService.use('en'));
		try {
			return await this.generateGuaranteePdf(guaranteeId, requestDate);
		} finally {
			await firstValueFrom(this.translateService.use(currentLanguage));
		}
	}

	private getHeaderColumnX(doc: jsPDF, sidePadding: number, requestDate: string):
	{ dateX: number; guaranteeIdX: number; projectNameX: number } {
		const pageWidth = doc.internal.pageSize.getWidth();
		doc.setFont('helvetica', 'normal');
		doc.setFontSize(12);

		const projectName = this.guarantee.name.slice(0, this.projectNameMaxLength);
		const projectNameWidth = doc.getTextWidth(projectName);
		const projectNameLeftEdge = pageWidth - sidePadding - projectNameWidth;

		const columnGap = 24;
		const dateColumnEnd = sidePadding + doc.getTextWidth(requestDate);
		const centerBoxLeft = dateColumnEnd + columnGap;
		const centerBoxRight = projectNameLeftEdge - columnGap;
		const guaranteeIdX = (centerBoxLeft + centerBoxRight) / 2;

		return {
			dateX: sidePadding,
			guaranteeIdX,
			projectNameX: pageWidth - sidePadding
		};
	}

	private drawHeaderAndFooter(doc: jsPDF, guaranteeId: string, requestDateValue = this.guarantee.requestDate) {
		const pageCount = doc.getNumberOfPages();
		const pageWidth = doc.internal.pageSize.getWidth();
		const pageHeight = doc.internal.pageSize.getHeight();
		const sidePadding = 40;
		const headerHeight = 54;
		const footerHeight = 24;

		const datePipe = new DatePipe();
		const requestDate = requestDateValue
			? String(datePipe.transform(requestDateValue, 'date') ?? '')
			: '';
		const { dateX, guaranteeIdX, projectNameX } = this.getHeaderColumnX(doc, sidePadding, requestDate);

		const columns = [
			{
				align: <const> 'left',
				x: dateX,
				label: this.translationService.getTR('guarantee.request.date'),
				value: requestDate,
			},
			{
				align: <const> 'center',
				x: guaranteeIdX,
				label: this.translationService.getTR('guarantee.request.id'),
				value: guaranteeId,
			},
			{
				align: <const> 'right',
				x: projectNameX,
				label: this.translationService.getTR('guarantee.project.name'),
				value: this.guarantee.name,
			},
		];
		const pageLabel = this.translationService.getTR('guarantee.pdf.page');

		for (let page = 1; page <= pageCount; page++) {
			doc.setPage(page);
			doc.setFont('helvetica', 'normal');

			doc.setFillColor(65, 65, 65);
			doc.rect(0, 0, pageWidth, headerHeight, 'F');
			doc.setTextColor(255, 255, 255);
			for (const column of columns) {
				doc.setFontSize(9);
				doc.text(column.label, column.x, 20, { align: column.align });
				doc.setFontSize(12);
				doc.text(column.value, column.x, 38, { align: column.align });
			}

			doc.setFillColor(65, 65, 65);
			doc.rect(0, pageHeight - footerHeight, pageWidth, footerHeight, 'F');
			doc.setTextColor(255, 255, 255);
			doc.setFontSize(10);
			doc.text(
				`${ pageLabel } ${ page }/${ pageCount }`,
				pageWidth - sidePadding,
				pageHeight - 8,
				{ align: 'right' }
			);
		}
	}
}
